<template>
  <b-card no-body class="weather-widget mt-2">
    <div slot="header" class="d-flex justify-content-between align-items-center">
      <span><i class="fa fa-cloud mr-1"></i> Local Weather</span>
      <small class="text-muted ml-2" style="font-size:0.72rem;">{{ rainfallRangeLabel }}</small>
    </div>

    <b-card-body class="py-2 px-3">

      <div v-if="!lat || !lng" class="text-muted small text-center py-1">
        No device location set.
      </div>

      <div v-else-if="loading" class="text-center py-2">
        <b-spinner variant="primary" small></b-spinner>
        <span class="ml-2 text-muted small">Fetching weather&hellip;</span>
      </div>

      <div v-else-if="error" class="text-muted small py-1">
        <i class="fa fa-exclamation-triangle mr-1"></i> {{ error }}
      </div>

      <div v-else-if="current">
        <b-row class="align-items-center">

          <!-- Current conditions -->
          <b-col cols="auto" class="border-right pr-3 mr-1">
            <div class="d-flex align-items-center">
              <i :class="['fa', currentIcon, 'mr-2']" style="font-size:1.4rem;color:#5c9bd6;min-width:1.5rem;"></i>
              <div>
                <div class="font-weight-bold" style="font-size:1rem;line-height:1.2;">
                  {{ current.temperature_2m !== null ? Math.round(current.temperature_2m) + '°F' : '—' }}
                  <span class="text-muted font-weight-normal" style="font-size:0.72rem;">&nbsp;/&nbsp;{{ currentTempC !== null ? currentTempC + '°C' : '—' }}</span>
                </div>
                <div class="text-muted" style="font-size:0.72rem;">{{ currentDescription }}</div>
                <div class="text-muted" style="font-size:0.7rem;">Humidity: {{ current.relative_humidity_2m !== null ? current.relative_humidity_2m + '%' : '—' }}</div>
              </div>
            </div>
          </b-col>

          <!-- 7-day forecast strip -->
          <b-col v-if="forecastDays.length" class="border-right pr-3">
            <div class="text-muted text-uppercase mb-1" style="font-size:0.58rem;letter-spacing:0.06em;">7-Day Forecast</div>
            <div class="d-flex">
              <div
                v-for="day in forecastDays"
                :key="day.date"
                class="forecast-cell flex-fill text-center"
              >
                <div class="text-muted" style="font-size:0.58rem;">{{ day.label }}</div>
                <i :class="['fa', day.icon]" style="font-size:0.75rem;color:#5c9bd6;"></i>
                <div class="font-weight-bold" style="font-size:0.72rem;">{{ day.tempMax }}&deg;</div>
                <div class="text-muted" style="font-size:0.58rem;">{{ day.precipProb }}%</div>
              </div>
            </div>
          </b-col>

          <!-- Historical rainfall chart -->
          <b-col md="5" class="pl-3">
            <div class="text-muted text-uppercase mb-1" style="font-size:0.58rem;letter-spacing:0.06em;">
              Rainfall (in) &mdash; {{ rainfallRangeLabel }}
            </div>
            <highcharts v-if="rainfallChartOptions" :options="rainfallChartOptions"></highcharts>
            <div v-else class="text-muted small text-center py-2">No precipitation data available.</div>
          </b-col>

        </b-row>
        <div class="mt-1">
          <small class="text-muted" style="font-size:0.58rem;">Source: Open-Meteo</small>
        </div>
      </div>

    </b-card-body>
  </b-card>
</template>

<script>
var WMO_CODES = {
  0:  { label: 'Clear sky',          icon: 'fa-sun' },
  1:  { label: 'Mainly clear',       icon: 'fa-sun' },
  2:  { label: 'Partly cloudy',      icon: 'fa-cloud' },
  3:  { label: 'Overcast',           icon: 'fa-cloud' },
  45: { label: 'Fog',                icon: 'fa-cloud' },
  48: { label: 'Icy fog',            icon: 'fa-cloud' },
  51: { label: 'Light drizzle',      icon: 'fa-tint' },
  53: { label: 'Drizzle',            icon: 'fa-tint' },
  55: { label: 'Heavy drizzle',      icon: 'fa-tint' },
  61: { label: 'Light rain',         icon: 'fa-tint' },
  63: { label: 'Rain',               icon: 'fa-tint' },
  65: { label: 'Heavy rain',         icon: 'fa-tint' },
  71: { label: 'Light snow',         icon: 'fa-snowflake' },
  73: { label: 'Snow',               icon: 'fa-snowflake' },
  75: { label: 'Heavy snow',         icon: 'fa-snowflake' },
  77: { label: 'Snow grains',        icon: 'fa-snowflake' },
  80: { label: 'Light showers',      icon: 'fa-tint' },
  81: { label: 'Showers',            icon: 'fa-tint' },
  82: { label: 'Heavy showers',      icon: 'fa-tint' },
  85: { label: 'Snow showers',       icon: 'fa-snowflake' },
  86: { label: 'Heavy snow showers', icon: 'fa-snowflake' },
  95: { label: 'Thunderstorm',       icon: 'fa-bolt' },
  96: { label: 'Thunderstorm',       icon: 'fa-bolt' },
  99: { label: 'Thunderstorm',       icon: 'fa-bolt' }
}

function wmo (code) {
  return WMO_CODES[code] || { label: 'Unknown', icon: 'fa-cloud' }
}

function dateKey (d) {
  var y = d.getFullYear()
  var m = String(d.getMonth() + 1).padStart(2, '0')
  var day = String(d.getDate()).padStart(2, '0')
  return y + '-' + m + '-' + day
}

function daysBetween (a, b) {
  return Math.round(Math.abs(b.getTime() - a.getTime()) / (24 * 3600 * 1000))
}

export default {
  name: 'WeatherWidget',
  props: {
    lat:       { type: Number, default: null },
    lng:       { type: Number, default: null },
    // [startDate, endDate] — same dateRange array used by the sensor graphs
    dateRange: { type: Array, default: null }
  },
  data () {
    return {
      loading: false,
      error: null,
      current: null,
      forecastDays: [],
      rainfallChartOptions: null,
      rainfallRangeLabel: ''
    }
  },
  computed: {
    currentTempC () {
      if (!this.current || this.current.temperature_2m === null) return null
      return Math.round((this.current.temperature_2m - 32) * 5 / 9 * 10) / 10
    },
    currentDescription () {
      if (!this.current) return ''
      return wmo(this.current.weather_code).label
    },
    currentIcon () {
      if (!this.current) return 'fa-cloud'
      return wmo(this.current.weather_code).icon
    },
    rangeStart () {
      if (this.dateRange && this.dateRange[0]) return new Date(this.dateRange[0])
      return new Date(Date.now() - 7 * 24 * 3600 * 1000)
    },
    rangeEnd () {
      if (this.dateRange && this.dateRange[1]) return new Date(this.dateRange[1])
      return new Date()
    }
  },
  mounted () {
    if (this.lat && this.lng) this.fetchWeather()
  },
  watch: {
    lat:       function (val) { if (val && this.lng) this.fetchWeather() },
    lng:       function (val) { if (val && this.lat) this.fetchWeather() },
    dateRange: function () { if (this.lat && this.lng) this.fetchWeather() }
  },
  methods: {
    async fetchWeather () {
      this.loading = true
      this.error = null
      this.current = null
      this.forecastDays = []
      this.rainfallChartOptions = null
      this.rainfallRangeLabel = ''

      try {
        // ── 1. Current conditions + 7-day forecast (always present-day) ───────
        var fcUrl = 'https://api.open-meteo.com/v1/forecast' +
          '?latitude=' + this.lat.toFixed(4) +
          '&longitude=' + this.lng.toFixed(4) +
          '&current=temperature_2m,relative_humidity_2m,weather_code' +
          '&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max' +
          '&temperature_unit=fahrenheit' +
          '&precipitation_unit=inch' +
          '&timezone=auto' +
          '&forecast_days=7'

        var fcRes = await fetch(fcUrl)
        if (!fcRes.ok) throw new Error('Open-Meteo forecast failed (' + fcRes.status + ')')
        var fcData = await fcRes.json()
        this.current = fcData.current

        var daily = fcData.daily
        if (daily && daily.time) {
          var todayKey = dateKey(new Date())
          this.forecastDays = daily.time.map(function (dateStr, i) {
            var d = new Date(dateStr + 'T12:00:00')
            return {
              date: dateStr,
              label: dateStr === todayKey ? 'Today' : d.toLocaleDateString('en-US', { weekday: 'short' }),
              icon: wmo(daily.weather_code[i]).icon,
              tempMax: daily.temperature_2m_max[i] !== null ? Math.round(daily.temperature_2m_max[i]) : '—',
              precipProb: daily.precipitation_probability_max[i] !== null ? Math.round(daily.precipitation_probability_max[i]) : 0
            }
          })
        }

        // ── 2. Precipitation over the selected date range ────────────────────
        var start = this.rangeStart
        var end = this.rangeEnd
        var today = new Date()

        // Archive lags ~1 day; cap the archive end at yesterday
        var yesterday = new Date(today.getTime() - 24 * 3600 * 1000)
        var archEnd = end > yesterday ? yesterday : end
        var archStart = start

        var totalDays = daysBetween(start, end)

        // Decide granularity: daily ≤31d, weekly ≤180d, monthly beyond
        var granularity
        if (totalDays <= 31) {
          granularity = 'daily'
        } else if (totalDays <= 180) {
          granularity = 'weekly'
        } else {
          granularity = 'monthly'
        }

        // Build range label for header
        var fmtOpts = { month: 'short', day: 'numeric', year: 'numeric' }
        this.rainfallRangeLabel = start.toLocaleDateString('en-US', fmtOpts) +
          ' – ' + end.toLocaleDateString('en-US', fmtOpts)

        // Fetch archive data
        var archUrl = 'https://archive-api.open-meteo.com/v1/archive' +
          '?latitude=' + this.lat.toFixed(4) +
          '&longitude=' + this.lng.toFixed(4) +
          '&start_date=' + dateKey(archStart) +
          '&end_date=' + dateKey(archEnd) +
          '&daily=precipitation_sum' +
          '&precipitation_unit=inch' +
          '&timezone=auto'

        var archRes = await fetch(archUrl)
        if (!archRes.ok) throw new Error('Open-Meteo archive failed (' + archRes.status + ')')
        var archData = await archRes.json()

        // Collect daily values from archive
        var rawDays = []  // [{ date: Date, precip: Number }]
        var archDaily = archData.daily
        if (archDaily && archDaily.time) {
          archDaily.time.forEach(function (dateStr, i) {
            var val = archDaily.precipitation_sum[i]
            rawDays.push({
              date: new Date(dateStr + 'T12:00:00'),
              precip: (val !== null && val !== undefined) ? val : 0
            })
          })
        }

        // If end date is today or in the future, append today from forecast
        if (end >= today && daily && daily.time) {
          var todayFcIdx = daily.time.indexOf(dateKey(today))
          if (todayFcIdx !== -1) {
            var todayVal = daily.precipitation_sum[todayFcIdx]
            rawDays.push({
              date: today,
              precip: (todayVal !== null && todayVal !== undefined) ? todayVal : 0
            })
          }
        }

        // Aggregate by granularity
        var categories = []
        var values = []

        if (granularity === 'daily') {
          rawDays.forEach(function (item) {
            var d = item.date
            categories.push(d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }))
            values.push(Math.round(item.precip * 100) / 100)
          })
        } else if (granularity === 'weekly') {
          var weekBuckets = {}
          var weekOrder = []
          rawDays.forEach(function (item) {
            // ISO week: Monday-anchored
            var d = new Date(item.date)
            var day = d.getDay() || 7
            d.setDate(d.getDate() - day + 1)  // back to Monday
            var wKey = dateKey(d)
            if (!(wKey in weekBuckets)) {
              weekBuckets[wKey] = 0
              weekOrder.push({ key: wKey, label: 'Wk ' + (d.getMonth() + 1) + '/' + d.getDate() })
            }
            weekBuckets[wKey] += item.precip
          })
          weekOrder.forEach(function (w) {
            categories.push(w.label)
            values.push(Math.round(weekBuckets[w.key] * 100) / 100)
          })
        } else {
          // Monthly
          var monthBuckets = {}
          var monthOrder = []
          rawDays.forEach(function (item) {
            var d = item.date
            var mKey = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0')
            var mLabel = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
            if (!(mKey in monthBuckets)) {
              monthBuckets[mKey] = 0
              monthOrder.push({ key: mKey, label: mLabel })
            }
            monthBuckets[mKey] += item.precip
          })
          monthOrder.forEach(function (m) {
            categories.push(m.label)
            values.push(Math.round(monthBuckets[m.key] * 100) / 100)
          })
        }

        if (categories.length) {
          var showDataLabels = granularity === 'daily' && totalDays <= 14
          this.rainfallChartOptions = {
            chart: { type: 'column', height: 130, margin: [10, 10, 35, 40], animation: false },
            title: { text: null },
            credits: { enabled: false },
            legend: { enabled: false },
            xAxis: {
              categories: categories,
              labels: {
                style: { fontSize: '10px' },
                rotation: totalDays > 14 ? -45 : 0
              }
            },
            yAxis: {
              title: { text: 'in', style: { fontSize: '10px' } },
              labels: { style: { fontSize: '10px' } },
              min: 0
            },
            tooltip: {
              valueSuffix: '"',
              pointFormat: '<b>{point.y:.2f}"</b>'
            },
            plotOptions: {
              column: {
                color: '#5c9bd6',
                borderWidth: 0,
                borderRadius: 2,
                dataLabels: {
                  enabled: showDataLabels,
                  format: '{point.y:.2f}"',
                  style: { fontSize: '9px', fontWeight: 'normal', textOutline: 'none' }
                }
              }
            },
            series: [{ name: 'Rainfall', data: values }]
          }
        }
      } catch (e) {
        this.error = e.message || 'Unable to load weather data'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.forecast-cell {
  padding: 4px 2px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  min-width: 0;
}
.forecast-cell + .forecast-cell {
  margin-left: 3px;
}
</style>
